import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchLocations,
  fetchLocation,
  createLocation,
  updateLocation,
  deleteLocation,
  restoreLocation,
} from '@/lib/api/locations'
import { broadcastInvalidation } from '@/lib/sync'
import type { LocationFormData } from '@/types/locations'
import type { RecordStatusFilter } from '@/types/record-status'
import { toast } from 'sonner'

/**
 * Query key factory for locations
 * Provides consistent keys for cache management
 */
export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (organizationId: string, recordStatus?: RecordStatusFilter) =>
    [...locationKeys.lists(), organizationId, recordStatus ?? 'active'] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
}

/**
 * Fetch all locations for current organization
 */
export function useLocations(recordStatus: RecordStatusFilter = 'active') {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: locationKeys.list(organizationId || '', recordStatus),
    queryFn: () => fetchLocations(organizationId!, recordStatus),
    enabled: !!organizationId,
  })
}

/**
 * Fetch a single location by ID
 */
export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: locationKeys.detail(id || ''),
    queryFn: () => fetchLocation(id!),
    enabled: !!id,
  })
}

/**
 * Create a new location
 */
export function useCreateLocation() {
  const queryClient = useQueryClient()
  const { currentOrganization, refreshLocations } = useTenant()

  return useMutation({
    mutationFn: (data: LocationFormData) =>
      createLocation(currentOrganization!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
      broadcastInvalidation([locationKeys.lists()])
      refreshLocations()
      toast.success('Location created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create location', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing location
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient()
  const { refreshLocations } = useTenant()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LocationFormData> }) =>
      updateLocation(id, data),
    onSuccess: (_, { id }) => {
      const keys = [locationKeys.lists(), locationKeys.detail(id)] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      refreshLocations()
      toast.success('Location updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update location', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a location (soft delete)
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient()
  const { refreshLocations } = useTenant()

  return useMutation({
    mutationFn: (id: string) => deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
      broadcastInvalidation([locationKeys.lists()])
      refreshLocations()
      toast.success('Location deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete location', {
        description: error.message,
      })
    },
  })
}

/**
 * Restore a soft-deleted location
 */
export function useRestoreLocation() {
  const queryClient = useQueryClient()
  const { refreshLocations } = useTenant()

  return useMutation({
    mutationFn: (id: string) => restoreLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
      broadcastInvalidation([locationKeys.lists()])
      refreshLocations()
      toast.success('Location restored successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to restore location', {
        description: error.message,
      })
    },
  })
}
